import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../../config';
import { useAuth } from '../../../context/AuthContext';
import PlatefullAILogo from '../../../images/PlatefullAILogoNB.svg';
import ToggleSwitch from '../../../components/ToggleSwitch/ToggleSwitch';
import StepProgress from '../../../components/StepProgress/StepProgress';
import SignOutLink from '../../../components/SignOutLink/SignOutLink';
import styles from '../Onboard.module.css';

interface HealthConcern {
  id: number;
  name: string;
}

interface UserHealthConcern {
  id: string;
  health_concern_id?: string;
  is_active: boolean;
  health_concern: {
    id: string;
    name: string;
    display_name?: string;
    description?: string;
    is_active?: boolean;
  };
  notes?: string;
}

interface HealthStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string;
  user: any;
  onStepClick?: (stepNumber: number) => void;
}

const HealthStep: React.FC<HealthStepProps> = ({ onNext, onBack, isLoading, error, user, onStepClick }) => {
  const { saveUserPreferencesToStorage } = useAuth();
  const [availableHealthConcerns, setAvailableHealthConcerns] = useState<HealthConcern[]>([]);
  const [isLoadingHealthConcerns, setIsLoadingHealthConcerns] = useState(false);
  const [userHealthConcerns, setUserHealthConcerns] = useState<UserHealthConcern[]>([]);

  // Format health concern names for display (remove underscores, capitalize)
  const formatHealthConcernName = (name: string): string => {
    // Handle common medical abbreviations
    const medicalAbbreviations: { [key: string]: string } = {
      'ibs': 'IBS',
      'sibo': 'SIBO',
      'adhd': 'ADHD',
      'ptsd': 'PTSD',
      'ocd': 'OCD',
      'bpd': 'BPD',
      'gad': 'GAD',
      'pms': 'PMS',
      'pcos': 'PCOS',
      'endometriosis': 'Endometriosis'
    };
    
    const lowerName = name.toLowerCase();
    if (medicalAbbreviations[lowerName]) {
      return medicalAbbreviations[lowerName];
    }
    
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Health concern explanations
  const getHealthConcernExplanation = (name: string): string => {
    const explanations: { [key: string]: string } = {
      'diabetes': 'Manage blood sugar levels with low-carb, high-fiber foods',
      'heart_disease': 'Focus on heart-healthy foods, low sodium, and healthy fats',
      'high_blood_pressure': 'Limit sodium, increase potassium, and avoid processed foods',
      'high_cholesterol': 'Reduce saturated fats, increase fiber, and choose lean proteins',
      'kidney_disease': 'Limit protein, phosphorus, and sodium for kidney health',
      'liver_disease': 'Avoid alcohol, limit processed foods, and focus on whole foods',
      'digestive_issues': 'Eat smaller meals, avoid trigger foods, and increase fiber gradually',
      'ibs': 'Low FODMAP diet to reduce digestive symptoms and trigger foods',
      'sibo': 'Low FODMAP diet and specific carbohydrate diet to reduce bacterial overgrowth',
      'autoimmune_disease': 'Anti-inflammatory diet with whole foods and limited processed items',
      'cancer': 'Nutrient-dense foods to support immune system and recovery',
      'pregnancy': 'Avoid alcohol, raw fish, unpasteurized dairy, and high-mercury fish',
      'breastfeeding': 'Increased caloric and nutrient needs for milk production',
      'elderly_nutrition': 'Focus on nutrient density and easy-to-digest foods',
      'weight_management': 'Balanced approach to healthy weight gain or loss',
      'eating_disorders': 'Structured meal planning and nutritional rehabilitation',
      'food_allergies': 'Strict avoidance of specific allergens and cross-contamination',
      'metabolic_syndrome': 'Low-carb, anti-inflammatory diet to improve insulin sensitivity',
      'thyroid_disorders': 'Support thyroid function with selenium, iodine, and balanced nutrition',
      'osteoporosis': 'Calcium and vitamin D rich foods for bone health',
      'anemia': 'Iron-rich foods and vitamin C to enhance absorption',
      'migraines': 'Identify and avoid trigger foods that may cause headaches'
    };
    
    return explanations[name.toLowerCase()] || 'Specialized dietary considerations for this health condition';
  };

  // Initialize from user preferences
  useEffect(() => {
    if (user?.preferences?.healthConcerns) {
      setUserHealthConcerns(user.preferences.healthConcerns);
    } else {
      setUserHealthConcerns([]);
    }
  }, [user?.preferences?.healthConcerns]);

  const loadAvailableHealthConcerns = async () => {
    if (!user) return;

    setIsLoadingHealthConcerns(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/user/health-concerns/available`, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      setAvailableHealthConcerns(response.data);
    } catch (error) {
      console.error('Error loading health concerns:', error);
    } finally {
      setIsLoadingHealthConcerns(false);
    }
  };

  useEffect(() => {
    loadAvailableHealthConcerns();
  }, [user]);

  const handleHealthConcernToggle = async (healthConcernId: number) => {
    if (!user) return;

    const existingConcern = userHealthConcerns.find((uc: UserHealthConcern) => {
      // Handle both data formats
      if (uc.health_concern_id) {
        // Format 1: has health_concern_id field
        return uc.health_concern_id === healthConcernId.toString();
      } else if (uc.health_concern && uc.health_concern.id) {
        // Format 2: health concern data directly in health_concern object
        return uc.health_concern.id === healthConcernId.toString();
      }
      return false;
    });
    
    const isCurrentlySelected = !!existingConcern;
    const selectedCount = userHealthConcerns.length;
    
    // If trying to select and already at max (3), don't allow
    if (!isCurrentlySelected && selectedCount >= 3) {
      return;
    }

    // Update local state immediately for better UX
    let updated: UserHealthConcern[];
    let tempId: string | null = null;
    
    if (existingConcern) {
      // Remove existing concern
      updated = userHealthConcerns.filter((uc: UserHealthConcern) => uc.id !== existingConcern.id);
    } else {
      // Create new concern with temporary ID
      tempId = Date.now().toString();
      const newConcern: UserHealthConcern = {
        id: tempId,
        health_concern_id: healthConcernId.toString(),
        is_active: true, // This represents the health concern being available, not selected
        health_concern: {
          id: healthConcernId.toString(),
          name: availableHealthConcerns.find(hc => hc.id === healthConcernId)?.name || ''
        }
      };
      updated = [...userHealthConcerns, newConcern];
    }

    // Update local state immediately
    setUserHealthConcerns(updated);
    
    // Save to localStorage for persistence
    saveUserPreferencesToStorage({
      dietary: user?.preferences?.dietary || [],
      healthConcerns: updated,
      ingredientPreferences: user?.preferences?.ingredientPreferences || []
    });

    try {
      if (existingConcern) {
        // Remove existing concern
        await axios.delete(
          `${config.API_BASE_URL}/user/health-concerns/${existingConcern.id}`,
          {
            headers: {
              'Authorization': `Bearer ${user.accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new concern
        const requestData = {
          health_concern_id: healthConcernId,
          notes: ''
        };
        
        const url = `${config.API_BASE_URL}/user/health-concerns`;
        const headers = {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        };
        
        console.log('=== ONBOARD: ADDING HEALTH CONCERN ===');
        console.log('URL:', url);
        console.log('Payload:', requestData);
        console.log('Headers:', headers);
        
        const response = await axios.post(url, requestData, { headers });
        
        console.log('=== ONBOARD: API RESPONSE ===');
        console.log('Status:', response.status);
        console.log('Data:', response.data);
        console.log('Headers:', response.headers);

        // Update with real server data
        if (tempId) {
          const finalUpdated = updated.map((uc: UserHealthConcern) =>
            uc.id === tempId ? response.data : uc
          );
          setUserHealthConcerns(finalUpdated);
          
          // Save final state to localStorage
          saveUserPreferencesToStorage({
            dietary: user?.preferences?.dietary || [],
            healthConcerns: finalUpdated,
            ingredientPreferences: user?.preferences?.ingredientPreferences || []
          });
        }
      }
    } catch (error: any) {
      console.error('=== ONBOARD: ERROR UPDATING HEALTH CONCERN ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      console.error('Error config:', error.config);
      
      // Revert local state if API call fails
      setUserHealthConcerns(userHealthConcerns);
      
      // Revert localStorage
      saveUserPreferencesToStorage({
        dietary: user?.preferences?.dietary || [],
        healthConcerns: userHealthConcerns,
        ingredientPreferences: user?.preferences?.ingredientPreferences || []
      });
    }
  };

  const isHealthConcernActive = (healthConcernId: number): boolean => {
    return userHealthConcerns.some((uc: UserHealthConcern) => {
      // Handle both data formats
      if (uc.health_concern_id) {
        // Format 1: has health_concern_id field
        return uc.health_concern_id === healthConcernId.toString();
      } else if (uc.health_concern && uc.health_concern.id) {
        // Format 2: health concern data directly in health_concern object
        return uc.health_concern.id === healthConcernId.toString();
      }
      return false;
    });
  };

  const handleNext = () => {
    onNext({});
  };

  if (isLoadingHealthConcerns) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.stepHeader}>
          <div className={styles.logoContainer}>
            <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
          </div>
          <h2 className={styles.stepTitle}>
            Health
          </h2>
        </div>
        
        <StepProgress 
          currentStep={3} 
          totalSteps={4} 
          stepNames={['Account', 'Dietary', 'Health', 'Favorites']}
          onStepClick={onStepClick}
        />
        
        <div className={styles.stepDescription}>
          <p>
            Loading your health options...
          </p>
        </div>
        <div className={styles.stepContent}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <span className={styles.loadingSpinner}></span>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <h2 className={styles.stepTitle}>
          Health
        </h2>
      </div>
      
      <StepProgress 
        currentStep={3} 
        totalSteps={4} 
        stepNames={['Account', 'Dietary', 'Health', 'Favorites']}
        onStepClick={onStepClick}
      />
      
      <div className={styles.stepDescription}>
        <div className={styles.stepDescriptionText}>
          <p>
            Select up to 3 health concerns that affect your dietary needs
          </p>
        </div>
        <div className={styles.stepCount}>
          <p className={styles.stepSubDescription}>
            {userHealthConcerns.length}/3
          </p>
        </div>
      </div>

      <div className={styles.stepContent}>
        <div className={styles.preferencesGrid}>
          {availableHealthConcerns.map((concern: HealthConcern) => {
            const isChecked = isHealthConcernActive(concern.id);
            const selectedCount = userHealthConcerns.length;
            const isAtMax = selectedCount >= 3;
            const isDisabled = isLoading || (isAtMax && !isChecked);
            
            return (
              <ToggleSwitch
                key={concern.id}
                id={`onboard-health-${concern.id}`}
                checked={isChecked}
                onChange={() => handleHealthConcernToggle(concern.id)}
                disabled={isDisabled}
                label={formatHealthConcernName(concern.name)}
                explanation={getHealthConcernExplanation(concern.name)}
              />
            );
          })}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={onBack}
          className={styles.secondaryButton}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isLoading}
          className={styles.primaryButton}
        >
          <span>
            {isLoading && <span className={styles.loadingSpinner}></span>}
            {isLoading ? 'Saving...' : 'Next'}
          </span>
        </button>
      </div>
      
      <SignOutLink />
    </div>
  );
};

export default HealthStep;
